import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminVerification = () => {
  const { user } = useAuth();
  const [tradies, setTradies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    fetchTradies();
  }, [filter]);

  const fetchTradies = async () => {
    setLoading(true);
    try {
      // Fetch real tradie profiles from database
      const response = await axios.get(`${API}/admin/tradies`);
      const allTradies = response.data.tradies || [];
      
      // Filter based on selected status
      let filtered = allTradies;
      if (filter !== 'all') {
        filtered = allTradies.filter(t => t.abn_status === filter || t.licence_status === filter || t.insurance_status === filter);
      }
      
      setTradies(filtered);
    } catch (error) {
      console.error('Failed to load tradies:', error);
      toast.error('Failed to load verification queue');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (tradieId, field, status, reason = '') => {
    try {
      await axios.patch(`${API}/admin/tradies/${tradieId}/verify`, {
        field,
        status,
        reason
      });
      
      toast.success(`Verification ${status === 'verified' ? 'approved' : 'rejected'}`);
      fetchTradies();
    } catch (error) {
      console.error('Failed to update verification:', error);
      toast.error('Failed to update verification');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      verified: <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Verified</Badge>,
      pending: <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pending</Badge>,
      rejected: <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Rejected</Badge>,
      not_provided: <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">Not Provided</Badge>,
    };
    return badges[status] || badges.not_provided;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Tradie Verification</h1>
          <p className="text-muted-foreground">Review and approve tradie credentials</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2">
          {['pending', 'verified', 'rejected', 'all'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Verification Queue */}
        <Card>
          <CardHeader>
            <CardTitle>Verification Queue ({tradies.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : tradies.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
                <p className="text-muted-foreground">No tradies in {filter} status</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tradies.map((tradie) => (
                  <Card key={tradie.id} className="border-2 border-border">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-foreground">{tradie.business_name}</h3>
                          <p className="text-sm text-muted-foreground">{tradie.tradie_type}</p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(tradie.abn_status)}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">ABN:</span>
                          <p className="font-mono">{tradie.abn || 'Not provided'}</p>
                          {getStatusBadge(tradie.abn_status)}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Licence:</span>
                          <p className="font-mono">{tradie.licence_number || 'Not provided'}</p>
                          {getStatusBadge(tradie.licence_status)}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Insurance:</span>
                          {getStatusBadge(tradie.insurance_status)}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Years Experience:</span>
                          <p>{tradie.years_experience} years</p>
                        </div>
                      </div>

                      {(tradie.abn_status === 'pending' || tradie.licence_status === 'pending' || tradie.insurance_status === 'pending') && (
                        <div className="space-y-2">
                          {tradie.abn_status === 'pending' && (
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleVerify(tradie.id, 'abn', 'verified')}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Approve ABN
                              </button>
                              <button
                                onClick={() => {
                                  const reason = prompt('Reason for rejection:');
                                  if (reason) handleVerify(tradie.id, 'abn', 'rejected', reason);
                                }}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                              >
                                <XCircle className="w-4 h-4" />
                                Reject ABN
                              </button>
                            </div>
                          )}
                          {tradie.licence_status === 'pending' && (
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleVerify(tradie.id, 'licence', 'verified')}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Approve Licence
                              </button>
                              <button
                                onClick={() => {
                                  const reason = prompt('Reason for rejection:');
                                  if (reason) handleVerify(tradie.id, 'licence', 'rejected', reason);
                                }}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                              >
                                <XCircle className="w-4 h-4" />
                                Reject Licence
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminVerification;
